import React, { useRef, useEffect } from "react";
import { openNotification, showMultipleToasts } from "../../utils/apiSetup";
import { useTranslation } from "react-i18next";
import { customerDetailsPath, editQuotePath, viewPolicyPath } from "../../router/paths";
import { useHistory, useLocation } from "react-router-dom";
import { getFeatureFlagsConfiguration } from "../../utils/getGlobalConfiguration.js";
import { featureFlags } from "../FeatureFlag/featureFlagConst";
import { jotaiStore } from "../../root";
import { agentApiEndpointAtom } from "../../atoms/atoms";
import { isCustomEvent } from "../../widgets/guards";

interface AgentSalesFlowWidgetProps {
  policyUuid?: string;
  partyId?: string;
  localization?: string;
  mode?: string;
  setPolicyStatus?: (status: string) => void;
  queryParameters?: string;
  disableProductSelection?: boolean;
  enableClauseSearch?: boolean;
  enableRiskToggleOnLeft?: boolean;
  enableDatepicker?: boolean;
  history?: {
    push: (path: string) => void;
  };
  match?: {
    params?: {
      customerid?: string;
    };
  };
  policyUuidVersionDate?: string;
  enableNewWidget?: boolean;
  widgetFeatureFlags?: {
    enableCalculatePremiumButton?: boolean;
    enablePremiumRibbonOnTop?: boolean;
    enablePremiumRibbonOnBottom?: boolean;
  };
}

type Errors = Array<{
  message: string;
  title: string;
  errorCode: string;
  externalErrorCode: string;
  source: string;
  severityLevel: string;
  timestamp: string;
  items?: string[];
}>;
type Messages = Array<{
  severityLevel: string;
  message: string;
  messageCode: string;
  title: string;
  timestamp: string;
  externalMessageCode: string;
  source: string;
  items?: string[];
}>;

export const AgentSalesFlowWidget = ({
  policyUuid,
  partyId,
  localization,
  mode = "",
  setPolicyStatus,
  queryParameters,
  disableProductSelection,
  enableClauseSearch,
  enableRiskToggleOnLeft,
  enableDatepicker,
  history,
  match,
  policyUuidVersionDate,
  enableNewWidget,
  widgetFeatureFlags,
}: AgentSalesFlowWidgetProps) => {
  const widgetRef = useRef<HTMLElement>(null);
  const { t } = useTranslation();
  const routerHistory = useHistory();
  const { pathname } = useLocation();
  const urlParts = pathname.split("/");
  const customerId = urlParts[3];
  const uuid = urlParts[5];

  const showEditPolicyButton = getFeatureFlagsConfiguration(
    featureFlags.customer.policyView.showEditPolicyButtonSalesWidget,
  );
  const showEditCancelledPolicyButton = getFeatureFlagsConfiguration(
    featureFlags.customer.policyView.showEditCancelledPolicyButtonSalesWidget,
  );
  const showQuoteOnPolicyButton = getFeatureFlagsConfiguration(
    featureFlags.customer.policyView.showQuoteOnPolicyButtonSalesWidget,
  );
  const showQuoteOnRenewalButton = getFeatureFlagsConfiguration(
    featureFlags.customer.policyView.showQuoteOnRenewalButtonSalesWidget,
  );

  const litFeatureFlagsStringified = JSON.stringify({
    showEditPolicyButton,
    showQuoteOnPolicyButton,
    showQuoteOnRenewalButton,
    showEditCancelledPolicyButton,
    enableTabsInNewWidget: true,
  });

  const { enableCalculatePremiumButton, enablePremiumRibbonOnTop, enablePremiumRibbonOnBottom } =
    widgetFeatureFlags || {};

  const featureFlagsStringified = JSON.stringify({
    enableEditPolicyButton: showEditPolicyButton,
    enableEditCancelledPolicyButton: showEditCancelledPolicyButton,
    enableQuoteOnPolicyButton: showQuoteOnPolicyButton,
    enableQuoteOnRenewalButton: showQuoteOnRenewalButton,
    enableProductSelection: !disableProductSelection,
    enableClauseSearch: enableClauseSearch,
    enableRiskClauseToggleOnLeft: enableRiskToggleOnLeft,
    enableCalculatePremiumButton,
    enablePremiumRibbonOnTop,
    enablePremiumRibbonOnBottom,
  });

  const newSalesFlowWidgetEnabled = getFeatureFlagsConfiguration("newSalesFlowWidget.enabled");
  const isNewWidget = enableNewWidget ?? newSalesFlowWidgetEnabled;

  useEffect(() => {
    const widgetElement = widgetRef.current;

    const handleOnSuccessAction = (event: Event) => {
      if (
        !isCustomEvent<{
          messages: Messages;
          type: string;
          actionType: string;
          action: string;
          payload: any;
          queryParameters?: Record<string, string>;
          policyStatus: string;
        }>(event)
      ) {
        return;
      }

      const { type, actionType, action, payload, messages, policyStatus } = event.detail;
      const flowMode = queryParameters ? JSON.parse(queryParameters)?.mode : null;

      if (action === "edit-policy" || action === "edit-quote") {
        routerHistory.push(
          editQuotePath({
            customerId: partyId,
            quoteId: policyUuid,
            queryParameters: event.detail.queryParameters,
          }),
        );
      }

      if (action === "fetch-policy" && setPolicyStatus) {
        setPolicyStatus(policyStatus);
      }

      if (flowMode && flowMode === "setinforce" && match?.params?.customerid) {
        history?.push(customerDetailsPath(match.params.customerid));
      }

      if (action === "update-policy" && !["QoP", "QoR"].includes(policyStatus)) {
        history?.push(viewPolicyPath(customerId, uuid).toString());
      }

      // TODO Domantas: This is for old widget, remove once lit widget is removed
      if (type === "mta" && actionType === "primary" && payload.successful) {
        const policyStatus = payload.data[0].salesProductHeader.policyStatus;

        if (!["QoP", "QoR"].includes(policyStatus)) {
          history?.push(viewPolicyPath(customerId, uuid).toString());
        }
      }

      if (messages) {
        messages.forEach((message: { title: string; message: string; severityLevel: string; messageCode: string }) =>
          openNotification({
            title: message.title,
            message: message.message,
            severityLevel: message.severityLevel,
            messageCode: message.messageCode,
          }),
        );
      }
    };

    const handleMessage = (event: Event) => {
      if (!isCustomEvent<{ messages?: Messages }>(event)) {
        return;
      }
      showMultipleToasts(event.detail.messages);
    };

    const handleError = (event: Event) => {
      if (!isCustomEvent<{ messages?: Messages; errors?: Errors }>(event)) {
        return;
      }
      if (event.detail?.messages?.length) {
        showMultipleToasts(event.detail.messages);
      }

      if (event.detail?.errors?.length) {
        showMultipleToasts(event.detail.errors);
      }
    };

    const handlePolicyLoad = (event: Event) => {
      if (!isCustomEvent<any>(event)) {
        return;
      }
      if (setPolicyStatus) {
        try {
          setPolicyStatus(event.detail.salesPolicy.salesProductHeader.policyStatus);
        } catch {
          console.error("Policy load failed");
        }
      }
    };

    const handleEditPolicy = (event: Event) => {
      if (!isCustomEvent<{ partyId: string; policyUuid: string; queryParameters?: Record<string, string> }>(event)) {
        return;
      }

      routerHistory.push(
        editQuotePath({
          customerId: partyId,
          quoteId: policyUuid,
          queryParameters: event.detail?.queryParameters,
        }),
      );
    };

    if (widgetElement) {
      widgetElement.addEventListener("success-widget-action", handleOnSuccessAction);
      widgetElement.addEventListener("widget-message", handleMessage);
      widgetElement.addEventListener("widget-error", handleError);
      widgetElement.addEventListener("widget-policy-load", handlePolicyLoad);
      widgetElement.addEventListener("widget-policy-edit", handleEditPolicy);
    }

    return () => {
      if (widgetElement) {
        widgetElement.removeEventListener("success-widget-action", handleOnSuccessAction);
        widgetElement.removeEventListener("widget-message", handleMessage);
        widgetElement.removeEventListener("widget-error", handleError);
        widgetElement.removeEventListener("widget-policy-load", handlePolicyLoad);
        widgetElement.removeEventListener("widget-policy-edit", handleEditPolicy);
      }
    };
  }, [t, policyUuidVersionDate, isNewWidget]);

  const litWidget = (
    <tia-agent-sales-flow-widget
      api={jotaiStore.get(agentApiEndpointAtom)}
      policyuuid={policyUuid}
      partyid={partyId}
      ref={widgetRef}
      mode={mode}
      localization={localization}
      queryparameters={queryParameters}
      disableproductselection={disableProductSelection?.toString()}
      enableclausesearch={enableClauseSearch?.toString()}
      enablerisktoggleonleft={enableRiskToggleOnLeft?.toString()}
      enabledatepicker={enableDatepicker?.toString()}
      enableeditpolicyhandling="false"
      policyuuidversiondate={policyUuidVersionDate?.split("T")[0]}
      featureflags={litFeatureFlagsStringified}
    />
  );

  const salesFlowWidget = (
    <div data-widget="sales-flow">
      <ds-sales-flow-widget
        api={jotaiStore.get(agentApiEndpointAtom)}
        policyuuid={policyUuid}
        partyid={partyId}
        ref={widgetRef}
        mode={mode === "preview" ? "view" : mode}
        translations={localization}
        queryparameters={queryParameters}
        policyuuidversiondate={policyUuidVersionDate?.split("T")[0]}
        featureflags={featureFlagsStringified}
      />
    </div>
  );

  if (isNewWidget) {
    return salesFlowWidget;
  }
  return litWidget;
};
